import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Icon, InputField } from "components";
import { Card } from "components/atoms/Card";
import { Empty } from "components/atoms/Empty";
import { Success } from "components/layouts/success";
import { useCallback, useId, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useAppDispatch } from "store/hooks";
import { resetPassword } from "store/slices/auth.slice";
import { resendVerificationEmail } from "store/slices/email.slice";
import constants from "utils/constants";
import { toastHelper } from "utils/helper";
import { formScheam } from "./schema";
import STRINGS from "./strings.json";
export const NeedHelp = () => {
  const [emailResended, setEmailResended] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const toastId = useId();
  const form = useForm({
    defaultValues: {
      type: "",
      email: "",
    },
    resolver: zodResolver(formScheam),
  });

  const handleSubmit = useCallback(
    ({ email, type }: { email: string; type: string }) => {
      setLoading(true);
      toastHelper({
        message: "Sending email. Please wait...",
        type: "info",
        toastId,
      });
      if (type === "reset") {
        dispatch(resetPassword(email))
          .unwrap()
          .then(() => {
            toastHelper({
              message: "Password reset link has been sent to your email.",
              type: "success",
              toastId,
            });
            setEmailResended(true);
          })
          .catch(() => {
            toastHelper({
              message:
                "An error occurred while sending the verification email.",
              type: "error",
              toastId,
            });
          })
          .finally(() => {
            setLoading(false);
          });
        return;
      }
      dispatch(resendVerificationEmail({ email }))
        .unwrap()
        .then(() => {
          toastHelper({
            message: "Verification email sent. Please check your inbox.",
            type: "success",
            toastId,
          });
          setEmailResended(true);
        })
        .catch(() => {
          toastHelper({
            message: "An error occurred while sending the verification email.",
            type: "error",
            toastId,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );
  return (
    <div
      className="h-screen bg-primary flex justify-center flex-col"
      id="bg-container"
    >
      <div className="flex w-full justify-center items-center">
        <Card className="min-h-auto justify-center items-center gap-8 max-w-lg pb-10">
          {emailResended ? (
            <Success
              link="/signin"
              linkLabel={STRINGS.success_link_label}
              title={STRINGS.success_title}
              description={`Please check your inbox at ${form.watch("email")}`}
            />
          ) : (
            <>
              <div
                className={`h-full w-full flex flex-col sm:gap-2 justify-center items-center text-gray-400 text-base pb-8`}
                id="bg-container"
              >
                <div className="flex max-w-md flex-col justify-center text-center">
                  <h1 className="capitalize text-lg font-bold text-gray-200">
                    {STRINGS.empty_title}
                  </h1>
                  {STRINGS.empty_message}
                </div>
              </div>
              <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)}>
                  <div className="flex flex-col rounded-lg text-white-500 gap-4 overflow-hidden w-full">
                    <Controller
                      name="type"
                      render={({ field }) => (
                        <label className="cursor-pointer">
                          <input
                            type="radio"
                            name="type"
                            value="resend"
                            className="hidden peer"
                            checked={field.value === "resend"}
                            onChange={(event) => {
                              form.setFocus("email");
                              field.onChange(event.target.value);
                            }}
                          />
                          <div className="border-gray-700 rounded-lg p-4 border-2 flex gap-4 flex-row items-center text-xs peer-checked:text-primary-500 peer-checked:!border-primary-500 peer-checked:cursor-default peer-checked:bg-paper-700">
                            <Icon
                              name="client-name"
                              size={28}
                              className="text-white-500"
                            />
                            <div className="flex max-w-md flex-col justify-start text-left gap-2">
                              <h1 className="text-base font-semibold text-gray-200">
                                {STRINGS.resend_title}
                              </h1>
                              <span className="text-sm text-gray-400">
                                {STRINGS.resend_description}
                              </span>
                            </div>
                          </div>
                        </label>
                      )}
                    />
                    <Controller
                      name="type"
                      render={({ field }) => (
                        <label className="cursor-pointer">
                          <input
                            type="radio"
                            name="type"
                            value="reset"
                            className="hidden peer"
                            checked={field.value === "reset"}
                            onChange={(event) => {
                              form.setFocus("email");
                              field.onChange(event.target.value);
                            }}
                          />
                          <div className="border-gray-700 rounded-lg p-4 border-2 flex gap-4 flex-row items-center text-xs peer-checked:text-primary-500 peer-checked:!border-primary-500 peer-checked:cursor-default peer-checked:bg-paper-700">
                            <Icon
                              name="key"
                              size={28}
                              className="text-white-500"
                            />
                            <div className="flex max-w-md flex-col justify-start text-left gap-2">
                              <h1 className="text-base font-semibold text-gray-200">
                                {STRINGS.reset_title}
                              </h1>
                              <span className="text-sm text-gray-400">
                                {STRINGS.reset_description}
                              </span>
                            </div>
                          </div>
                        </label>
                      )}
                    />
                    <div
                      className={`${
                        form.watch("type") ? "max-h-[400px]" : " max-h-0"
                      } transition-all duration-300 ease-in-out`}
                    >
                      <Controller
                        name="email"
                        render={({ field }) => (
                          <InputField
                            name="email"
                            iconName="email"
                            placeholder={STRINGS.email_placeholder}
                            register={form.register}
                            errors={form.formState.errors.email}
                            label={STRINGS.email_label}
                            disabled={loading}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex w-full justify-center flex-col flex-1 gap-3 mt-6">
                    {form.watch("type") && (
                      <Button
                        isProcessing={loading}
                        isProcessingTitle="Sending..."
                        buttonContainerClassName="gap-2"
                        iconName="paperplane"
                        type="primary"
                        className="mt-2 mx-auto"
                        action="submit"
                        disabled={!form.formState.isValid}
                      >
                        {form.watch("type") === "resend"
                          ? STRINGS.submit_button_send_verification
                          : STRINGS.submit_button_send_password}
                      </Button>
                    )}
                    <div className="flex w-full justify-center items-center">
                      <Button
                        buttonContainerClassName="gap-2"
                        type="download"
                        iconName="arrow-left"
                        action="button"
                        className="mt-2"
                        onClick={() => (window.location.href = "/signin")}
                      >
                        {STRINGS.back_button}
                      </Button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </>
          )}
        </Card>
      </div>
      <div className="mt-8 w-full max-w-lg mx-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-300">
        <div className="flex items-start gap-3">
          <Icon name="info" size={16} className="text-white-500 mt-1" />
          <div className="flex flex-col gap-1">
            <p className="font-medium text-white">{STRINGS.info_title}</p>
            <p className="text-gray-400">
              {STRINGS.info_message}
              <br />
              <a
                target="_blank"
                href={`mailto:${constants.COMPANY_1_EMAIL_SUPPORT}`}
                className="text-primary-500 hover:underline font-medium"
              >
                {STRINGS.info_cta}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
